/**
 * Basic cost (excl. GST + cess) for a purchase line, kept consistent with the
 * line's cost price.
 *
 * Price rows store basePrice and costPrice independently, and a line can end
 * up pairing the cost of one row with the basic of another — e.g. a SKU with
 * a stray row "base 1 / cost 1.12" next to its real "64.51 / 72.25": picking
 * 72.25 kept the stored basic 1, and the GRN inherited it from the PO.
 *
 * A candidate basic is accepted only when grossing it up by GST + cess lands
 * within 2% (min ₹0.50) of the cost price, so honest invoice rounding still
 * passes. Candidates are tried in order (the price row matching the cost
 * first, then the stored line value); if none fits, the basic is derived from
 * the cost price.
 */
export function consistentBasicCost(
  costPrice: number,
  taxPercent: number,
  ...candidates: Array<number | string | null | undefined>
): number {
  const cost = Number(costPrice) || 0;
  const factor = 1 + (Number(taxPercent) || 0) / 100;
  const derived = +(cost / factor).toFixed(4);
  if (!(cost > 0)) {
    const first = candidates.map(Number).find((c) => c > 0);
    return first ?? 0;
  }
  const tolerance = Math.max(0.5, cost * 0.02);
  for (const c of candidates) {
    const basic = Number(c);
    if (basic > 0 && Math.abs(basic * factor - cost) <= tolerance) return basic;
  }
  return derived;
}

type PriceRowLike = { costPrice?: unknown; mrp?: unknown; sellingPrice?: unknown; basePrice?: unknown };

const same3 = (a: unknown, b: unknown) =>
  Math.abs(Number(a) - Number(b)) < 0.0005;

/**
 * The price row a saved purchase line was raised at: same cost AND MRP, else
 * same cost, else same MRP. A PO line stores cost and MRP but no selling
 * price, so a reopened line must take its SP from ITS row — `pricing[0]` is
 * just the variant's newest row and can belong to another cost (a stale SP
 * there failed "SP < cost" on save until the line was re-picked).
 */
export function pricingForLine<T extends PriceRowLike>(
  rows: T[] | null | undefined,
  costPrice: unknown,
  mrp: unknown,
): T | undefined {
  const list = rows || [];
  const hasCost = Number(costPrice) > 0;
  const hasMrp = Number(mrp) > 0;
  return (
    (hasCost && hasMrp
      ? list.find((p) => same3(p.costPrice, costPrice) && same3(p.mrp, mrp))
      : undefined) ??
    (hasCost ? list.find((p) => same3(p.costPrice, costPrice)) : undefined) ??
    (hasMrp ? list.find((p) => same3(p.mrp, mrp)) : undefined)
  );
}

/** The price row whose cost price equals `costPrice`, if any. */
export function pricingForCost(
  rows: Array<{ costPrice?: unknown; basePrice?: unknown }> | null | undefined,
  costPrice: number,
): { costPrice?: unknown; basePrice?: any } | undefined {
  return (rows || []).find((p) => +(p.costPrice as any) === +costPrice);
}
